/* eslint-disable n/prefer-global/process */
/** Initializes the Browser shell, authentication state, and ordered automatic logout lifecycle. */
import React, {
  useCallback, useEffect, useState,
} from 'react';
import {createRoot} from 'react-dom/client';
import {Provider, useDispatch} from 'react-redux';
import {getPreloadComplete} from '@qep/shared/src/preloads';
import {ComponentErrorBoundary} from '@qep/shared/src/components/ComponentErrorBoundary/ComponentErrorBoundary';
import {ThemeProvider} from '@qep/shared/src/components/ThemeProvider/ThemeProvider';
import '@qep/shared/src/styles/fonts/fonts.scss';
import {getQepSystem, useSleepLogout} from '@qep/shared/src';
import {isTestingQueryEnabled} from '@qep/shared/src/utils/runtime-config';
import {type IJsonModel} from 'flexlayout-react';
import splashScreen from '../assets/QEPSplash.jpg';
import '../index.scss';
import {getContainer} from './utils/utils.js';
import {cleanupSubscriptionsBeforeLogout} from './utils/logout.js';
import MobileApp from './mobile/mobile-app';
import {isMobileOrTabletDevice} from './mobile/device.js';
import {App} from './windowing/window-container.js';
import {applicationReducer, loadInitialState} from './state/reducer.js';
import store, {type AppDispatch} from './state/store.js';
import {Login} from './components/Login/login.js';
import './components/SplashScrim.scss';

/**
 * Checks for Service Worker updates without allowing unsupported browser capabilities or lookup failures to block startup.
 */
async function handleServiceWorkerUpdates() {
  const {serviceWorker} = navigator;
  if (
    !serviceWorker
    || typeof serviceWorker.getRegistrations !== 'function'
    || typeof serviceWorker.addEventListener !== 'function'
    || typeof globalThis.MessageChannel !== 'function'
  ) {
    return;
  }

  const messageChannel = new globalThis.MessageChannel();
  const UseServiceWorker = process.env.CACHE_WITH_SERVICE_WORKERS;
  let registrationsPromise: Promise<readonly ServiceWorkerRegistration[]>;
  try {
    registrationsPromise = serviceWorker.getRegistrations();
  } catch {
    return;
  }

  serviceWorker.controller?.postMessage({topic: 'version'}, [messageChannel.port2]);

  let refreshing = false;
  serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) {
      return;
    }

    refreshing = true;
    globalThis.location.reload();
  }, {once: true});

  let registrations: readonly ServiceWorkerRegistration[];
  try {
    registrations = await registrationsPromise;
  } catch {
    return;
  }

  await Promise.all(registrations.map(async registration => {
    if (!UseServiceWorker) {
      await registration.unregister();
      globalThis.location.reload();
      return;
    }

    if (registration) {
      if (registration.waiting) {
        registration.waiting.postMessage?.({type: 'SKIP_WAITING'});
      }

      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && serviceWorker.controller) {
              registration?.waiting?.postMessage({type: 'SKIP_WAITING'});
            }
          });
        }
      });
    }
  }));
}

/**
 * Enables browser-shell testing fallbacks only in non-production runtime environments.
 */
function isTestingModeEnabled() {
  return isTestingQueryEnabled(globalThis.location.search);
}

const createFallbackWorkspace = (): IJsonModel => ({
  global: {
    qepFallbackWorkspace: true,
    tabClassName: 'customTab',
    tabContentClassName: 'customContent',
    tabEnableRenderOnDemand: false,
    splitterEnableHandle: true,
  },
  borders: [],
  layout: {
    type: 'row',
    children: [
      {
        type: 'tabset',
        selected: 0,
        children: [
          {
            type: 'tab',
            name: 'Open component',
            component: 'selector',
          },
        ],
      },
    ],
  },
});

export const Main = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [browser] = useState(() => getContainer());
  const [loading, setLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(true);
  const [isMobile, setIsMobile] = useState(() => isMobileOrTabletDevice());

  useEffect(() => {
    /** Keeps the shell choice aligned with device and viewport changes after initial render. */
    function handleResize() {
      setIsMobile(isMobileOrTabletDevice());
    }

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const onReady = useCallback(async () => {
    setLoading(true);
    // Ensure splash screen is visible for at least 500ms
    const minSplash = 0;
    const start = Date.now();
    const isTestingMode = isTestingModeEnabled();
    const loadInitialStateDispatch = dispatch(loadInitialState(''));
    const loadInitialStatePromise = typeof loadInitialStateDispatch?.unwrap === 'function'
      ? loadInitialStateDispatch.unwrap()
      : Promise.resolve(loadInitialStateDispatch);
    const initialStateResult = await Promise.race([
      (async () => {
        try {
          await loadInitialStatePromise;
          return 'loaded' as const;
        } catch {
          return 'error' as const;
        }
      })(),
      new Promise<'timeout'>(resolve => {
        globalThis.setTimeout(() => {
          resolve('timeout');
        }, isTestingMode ? 7000 : 15_000);
      }),
    ]);

    if (initialStateResult !== 'loaded') {
      dispatch(applicationReducer.actions.updateWorkspaceState(createFallbackWorkspace()));
    }

    const elapsed = Date.now() - start;
    if (elapsed < minSplash) {
      setTimeout(() => {
        setLoading(false);
      }, minSplash - elapsed);
    } else {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    void handleServiceWorkerUpdates();
  }, []);

  const onAuthChange = useCallback(async (message: {message: any}) => {
    if (!message.message) {
      setShowLogin(true);
      setLoading(false);
    }

    if (message.message) {
      setShowLogin(false);
      void onReady();
    }
  }, [onReady]);

  useEffect(() => {
    browser.applicationClient.addListener(
      browser.applicationClient.APPLICATION_EVENTS.AUTH.AUTH_STATUS_CHANGE.topic,
      onAuthChange,
    );

    // Check authentication status and show login if needed
    const checkAuth = async () => {
      const authInfo = await browser.applicationClient.getAuthInfo();
      if (authInfo) {
        setShowLogin(false);
        void onReady();
      } else {
        setShowLogin(true);
        setLoading(false);
      }
    };

    void checkAuth();

    return () => {
      browser.applicationClient.removeListener(
        browser.applicationClient.APPLICATION_EVENTS.AUTH.AUTH_STATUS_CHANGE.topic,
        onAuthChange,
      );
    };
  }, [browser.applicationClient, onAuthChange, onReady]);

  const isAutoLogoutEnabled = getQepSystem().container.canUseFeature('autoLogout');

  /** Completes automatic server logout before permanently disposing API services. */
  const handleSleepLogout = useCallback(async () => {
    const container = getContainer();
    try {
      await container.authentication.logout();
    } catch (error) {
      console.error('Failed to log out automatically', error);
    } finally {
      cleanupSubscriptionsBeforeLogout();
    }
  }, []);

  useSleepLogout(handleSleepLogout, {enabled: isAutoLogoutEnabled});

  // Splash scrim overlay
  const splashScrim = loading
    ? (
      <div className='browser-splash-scrim'>
        <img className='browser-splash-image' src={splashScreen} alt='Loading...' />
      </div>
    )
    : null;

  return (
    <>
      {showLogin ? <Login /> : (isMobile && getQepSystem().container.canUseFeature('mobile') ? <MobileApp /> : <App />)}
      {!showLogin && splashScrim}
    </>
  );
};

const render = () => {
  const domNode = document.querySelector('#react-content');
  if (domNode) {
    const root = createRoot(domNode);
    root.render(<ComponentErrorBoundary>
      <ThemeProvider>
        <Provider store={store}>
          <Main />
        </Provider>
      </ThemeProvider>
    </ComponentErrorBoundary>);
  } else {
    console.error('Failed to find the root element');
  }
};

// eslint-disable-next-line unicorn/prefer-top-level-await
void getPreloadComplete().then(async () => {
  getQepSystem().container.onReady(render);
});
